import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight } from 'lucide-react';

const BlogSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  const blogPosts = [
    {
      slug: 'construccion-2026',
      title: 'La Construcción en 2026',
      excerpt: 'Nuevas técnicas y materiales revolucionan la industria: construcción modular, concreto autorreparable e impresión 3D.',
      image: 'https://customer-assets.emergentagent.com/job_eficapro-pro/artifacts/bsvp1xxv_blog-2026.jpg',
      category: 'Innovación'
    },
    {
      slug: 'tecnologia-ia',
      title: 'Tecnología e IA en Proyectos',
      excerpt: 'Drones con sensores LiDAR, software predictivo y BIM nivel 3 transforman la ejecución de obras.',
      image: 'https://customer-assets.emergentagent.com/job_eficapro-pro/artifacts/9hyg0b8p_blog-tecnologia.JPG',
      category: 'Tecnología'
    },
    {
      slug: 'construccion-mexico',
      title: 'La Construcción en México',
      excerpt: 'Nearshoring impulsa la demanda. Vivienda multifamiliar como solución para densificación urbana.',
      image: 'https://customer-assets.emergentagent.com/job_eficapro-pro/artifacts/tbk7xo9s_blog-mexico.jpg',
      category: 'Mercado'
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.15 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  return (
    <section 
      id="blog" 
      className="py-24 md:py-32 bg-white"
      data-testid="blog-section"
      ref={ref}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="mb-16"
        >
          <div className="flex items-center gap-4 mb-6">
            <div className="w-12 h-[2px] bg-[#2E75B6]"></div>
            <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#2E75B6]">
              Blog & Tendencias
            </p>
          </div>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#203864]">
              Innovación y Conocimiento
            </h2>
            <Link 
              to="/blog/construccion-2026"
              className="inline-flex items-center gap-2 text-[#2E75B6] font-semibold hover:gap-3 transition-all"
            >
              Ver todos los artículos
              <ArrowRight size={18} />
            </Link>
          </div>
        </motion.div>

        {/* Featured Article */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mb-12"
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 bg-[#203864] overflow-hidden">
            <div className="relative h-64 lg:h-auto">
              <img
                src="https://customer-assets.emergentagent.com/job_e366be57-2974-4669-af33-64f519b6752c/artifacts/5oh7m1wa_Imagen1.jpg"
                alt="Artículo destacado"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="p-8 lg:p-12 flex flex-col justify-center">
              <span className="text-[#2E75B6] text-sm font-bold uppercase tracking-wider mb-4">
                Artículo Destacado
              </span>
              <h3 className="text-2xl lg:text-3xl font-bold text-white mb-4">
                El Futuro de la Construcción: Innovación y Sostenibilidad
              </h3>
              <p className="text-white/70 mb-6">
                Descubre cómo las nuevas tecnologías y materiales están transformando la industria de la construcción en México y el mundo.
              </p>
              <Link
                to="/blog/construccion-2026"
                className="inline-flex items-center gap-2 text-white font-semibold hover:gap-3 transition-all w-fit"
              >
                Leer artículo
                <ArrowUpRight size={18} />
              </Link>
            </div>
          </div>
        </motion.div>

        {/* Blog Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
          className="grid grid-cols-1 md:grid-cols-3 gap-8"
        >
          {blogPosts.map((post, index) => (
            <motion.article
              key={post.slug}
              variants={itemVariants}
              className="group"
              data-testid={`blog-card-${index + 1}`}
            >
              {/* Image */}
              <div className="relative h-56 overflow-hidden mb-6">
                <img
                  src={post.image}
                  alt={post.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute top-4 left-4">
                  <span className="bg-[#2E75B6] text-white text-xs font-bold uppercase tracking-wider px-3 py-1">
                    {post.category}
                  </span>
                </div>
              </div>

              {/* Content */}
              <h3 className="text-xl font-bold text-[#203864] mb-3 group-hover:text-[#2E75B6] transition-colors">
                {post.title}
              </h3>

              <p className="text-[#7F7F7F] text-sm leading-relaxed mb-4">
                {post.excerpt}
              </p>

              <Link
                to={`/blog/${post.slug}`}
                className="inline-flex items-center gap-2 text-[#203864] font-semibold text-sm uppercase tracking-wider hover:text-[#2E75B6] hover:gap-3 transition-all"
                data-testid={`blog-link-${post.slug}`}
              >
                Leer más
                <ArrowRight size={14} />
              </Link>
            </motion.article>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default BlogSection;
